import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, BookOpen, Loader2, Pin } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { track } from "@/lib/ads";
import { rememberEmail } from "@/lib/knownEmail";
import { CHUTE_LINKS } from "@/lib/chuteGuides";
import { SITE_URL } from "@/lib/seo";

/** Liens vers les 8 guides chute de cheveux (maillage interne). */
export function ChuteGuidesNav({ current }) {
  return (
    <section data-testid="chute-guides-nav" className="mt-20">
      <p className="eyebrow mb-3">Nos guides chute de cheveux</p>
      <h2 className="mb-8 font-display text-3xl sm:text-4xl">Trouvez le guide qui correspond à votre situation</h2>
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {CHUTE_LINKS.filter((l) => l.to !== current).map((l) => (
          <Link key={l.to} to={l.to} data-testid={`chute-guide-link-${l.to.slice(1)}`}
            className="group flex items-center justify-between gap-3 rounded-sm border border-[var(--line)] bg-[var(--ink-2)] px-5 py-4 text-sm transition-colors duration-300 hover:border-[var(--gold)]">
            <span className="group-hover:text-[var(--gold-soft)]">{l.label}</span>
            <ArrowUpRight className="h-4 w-4 shrink-0 text-[var(--gold-soft)] transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
          </Link>
        ))}
      </div>
    </section>
  );
}

/** Bouton « Enregistrer sur Pinterest » (lien de partage officiel, aucun script tiers). */
export function PinButton({ path, image, text }) {
  const href = `https://www.pinterest.com/pin/create/button/?url=${encodeURIComponent(SITE_URL + path)}&media=${encodeURIComponent(SITE_URL + image)}&description=${encodeURIComponent(text)}`;
  return (
    <a data-testid="pinterest-save-button" href={href} target="_blank" rel="noopener noreferrer"
      className="mt-4 inline-flex h-10 items-center gap-2 rounded-full border border-[var(--line)] px-5 text-xs uppercase tracking-[0.18em] text-[var(--parchment)]/85 transition-colors hover:border-[var(--gold)] hover:text-[var(--gold-soft)]">
      <Pin className="h-3.5 w-3.5" aria-hidden="true" />Enregistrer sur Pinterest
    </a>
  );
}

/** Guide gratuit contre l'email : lien envoyé par email + accès direct à la version web. */
export function GuideOffer({ source }) {
  const [email, setEmail] = useState("");
  const [consent, setConsent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const send = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/guide/request", { email: email.trim(), consent });
      rememberEmail(email);
      try { localStorage.setItem("ozy_welcome_seen", "1"); } catch { /* stockage indisponible */ }
      track("Lead", { content_name: `guide_${source}` });
      setDone(true);
    } catch (err) { toast.error(errMsg(err)); }
    setBusy(false);
  };

  return (
    <section data-testid="guide-offer" className="mt-20 grid gap-8 overflow-hidden rounded-sm border border-[var(--gold)]/50 bg-[var(--ink-2)] md:grid-cols-[220px_minmax(0,1fr)]">
      <img src="/editorial/guide-routine-cover.webp" alt="Guide routine anti-chute Ozentya : flacon, peigne en bois et bonnet en satin" width="672" height="1000" loading="lazy" className="h-56 w-full object-cover md:h-full" />
      <div className="p-6 md:p-10">
        <p className="eyebrow mb-3 flex items-center gap-2"><BookOpen className="h-4 w-4" aria-hidden="true" />Guide gratuit</p>
        <h2 className="font-display text-3xl leading-tight sm:text-4xl">Routine anti-chute cheveux afro : <span className="italic gold-text">12 semaines</span> pour prendre soin de vos racines</h2>
        <p className="mt-4 max-w-2xl text-sm leading-relaxed text-[var(--parchment)]/85">Le programme semaine par semaine, la routine des tempes, les coiffures à éviter et un tableau de suivi photo. À lire en ligne ou à enregistrer en PDF.</p>
        {done ? (
          <div data-testid="guide-offer-done" className="mt-6 text-sm text-[var(--parchment)]">
            <p>C'est envoyé ! Le lien arrive dans votre boîte email (pensez aux courriers indésirables).</p>
            <Link to="/guide-routine-anti-chute" data-testid="guide-offer-open" className="mt-4 inline-flex h-12 items-center rounded-full bg-[var(--gold)] px-6 text-xs font-semibold uppercase tracking-[0.18em] text-[var(--ink)] hover:bg-[var(--gold-soft)]">Ouvrir le guide maintenant</Link>
          </div>
        ) : (
          <form onSubmit={send} className="mt-6 max-w-xl space-y-3">
            <div className="flex flex-col gap-3 sm:flex-row">
              <input data-testid="guide-offer-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="votre@email.fr" autoComplete="email"
                className="h-12 w-full rounded-sm border border-[var(--line)] bg-[var(--ink)] px-3 text-base outline-none focus:border-[var(--gold)]" />
              <button data-testid="guide-offer-submit" disabled={busy} className="flex h-12 shrink-0 items-center justify-center gap-2 rounded-full bg-[var(--gold)] px-6 text-xs font-semibold uppercase tracking-[0.18em] text-[var(--ink)] hover:bg-[var(--gold-soft)] disabled:opacity-40">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null}Recevoir le guide</button>
            </div>
            <label className="flex items-start gap-2 text-xs text-[var(--parchment)]/85"><input data-testid="guide-offer-consent" type="checkbox" required checked={consent} onChange={(e) => setConsent(e.target.checked)} className="mt-0.5" />J'accepte de recevoir le guide, les conseils et les offres Ozentya par email (désinscription en un clic).</label>
          </form>
        )}
      </div>
    </section>
  );
}
